import React from 'react';
import { inject, observer } from 'mobx-react';
import { Display } from '@/components/Display/Display';
import FileSearchDisplay from '@/components/FileSearch';
import { GrtAiMarkdown } from '@/components/Markdown/GrtAiMarkdown';
import CourseRecommend from '../CourseRecommend';
import MultimodalLearning from '../MultimodalLearning';
import Review from '@/pages/Web/PageComponents/Review';
import AiCard from '@/pages/Web/PageComponents/CaseSimulation/AiCard';
import CaseSimulation from '@/pages/pageComponents/CaseSimulation';
import ResearchPlanCard from '@/components/ResearchPlanCard';
import chatStore from '@/store/grtAi/chat';
import SimpleReportNotice from '@/pages/Web/PageComponents/SimpleReportNotice';
import AIPPT from '@/pages/Web/PageComponents/AIPPT';
import { handleAIPPTHistoryMessage as handleAIPPTHistoryMessageCore } from '@/pages/hooks/useAippt';
import IntelligentQA from '../IntelligentQA';
import PersonalRecommend from '../PersonalRecommend';
const AiRender = (props) => {
    const { item, message, store, sessionId, side, onClickCitation } = props;
    let {
        index,
        retrieval_result,
        web_result,
        content_type,
        processing,
        recommend_course_result,
        multimodal_learning_result,
        ai_evaluation,
        card_result,
        setDetailPanelOpen,
        setMultimodalReference,
        detailPanelOpen,
        activeDetailMessageId,
        setActiveDetailMessageId,
        getCurSessionMsgs,
        changeOriginMsgMapRef,
        currentSessionMapIdRef,
        createRequestByMsgId,

        getBaseParams,

        currentAgent,
        intelligent_qa_result,
        personal_recommend_result
    } = item;
    // 判断当前消息是否为会话中的最后一条消息（优先使用 store.sessionStore.sessionMap[sessionId].messages）
    const isLatestMessage = (msgIndex) => {
        try {
            // 优先从传入的 store 中取到当前会话消息列表
            const sessionMap = store && store.sessionStore && store.sessionStore.originMsgMap;
            const sessionEntry = sessionMap && sessionMap[sessionId];
            const msgsFromStore = sessionEntry && Array.isArray(sessionEntry.messages) ? sessionEntry.messages : null;
            const getId = (m) => (m && (m._id || m.temId || m.temIdStr || m.id)) || null;

            if (Array.isArray(msgsFromStore) && msgsFromStore.length > 0) {
                // 优先通过消息 id 比较，避免因索引差异导致误判
                const lastMsg = msgsFromStore[msgsFromStore.length - 1];
                const lastId = getId(lastMsg);
                const currentId = getId(item);
                if (lastId && currentId) {
                    return lastId === currentId;
                }
                // 回退到索引比较（兼容旧实现）
                return typeof msgIndex === 'number' && msgIndex === msgsFromStore.length - 1;
            }

            // 回退到可能传入的 getCurSessionMsgs 函数
            const msgsFromGetter = typeof getCurSessionMsgs === 'function' ? getCurSessionMsgs() : null;
            if (Array.isArray(msgsFromGetter)) {
                if (msgsFromGetter.length > 0) {
                    const lastMsg = msgsFromGetter[msgsFromGetter.length - 1];
                    const lastId = getId(lastMsg);
                    const currentId = getId(item);
                    if (lastId && currentId) {
                        return lastId === currentId;
                    }
                }
                return typeof msgIndex === 'number' && msgIndex === msgsFromGetter.length - 1;
            }
        } catch (e) {
            // 出问题时不要误判为最新，返回 false 避免让旧的计划按钮被激活
        }
        return false;
    };

    // 提取计划 JSON 的辅助函数（用于兼容 message / item.answerContent / item.content.*）
    const extractJSON = (text) => {
        if (!text || typeof text !== 'string') return null;
        if (!(text.includes('{') && text.includes('"title"') && text.includes('"steps"') && text.includes('}'))) return null;
        const possibleStarts = [];
        let idx = -1;
        while ((idx = text.indexOf('{', idx + 1)) !== -1) {
            possibleStarts.push(idx);
        }
        for (const start of possibleStarts) {
            let openBraces = 1;
            for (let i = start + 1; i < text.length; i++) {
                if (text[i] === '{') openBraces++;
                if (text[i] === '}') openBraces--;
                if (openBraces === 0) {
                    const jsonCandidate = text.substring(start, i + 1);
                    try {
                        const parsed = JSON.parse(jsonCandidate);
                        if (parsed && parsed.title && Array.isArray(parsed.steps)) {
                            return { json: parsed, start, end: i + 1 };
                        }
                    } catch (e) {
                        continue;
                    }
                }
            }
        }
        return null;
    };

    // 如果文本中包含 report-notice 的占位 HTML，则直接渲染 SimpleReportNotice 组件
    const tryRenderReportNotice = (text) => {
        if (!text || typeof text !== 'string') return null;
        if (!text.includes('data-type="report-notice"')) return null;

        const match = text.match(/data-unique-report-id="([^"]+)"/);
        const uniqueReportId = match && match[1] ? match[1] : item.task_id || item.reportId || null;
        const reportId = item.reportId || item.task_id || null;
        const threadId = item.session_id || item.threadId || null;

        return (
            <SimpleReportNotice
                key={`report-notice-${uniqueReportId || reportId || index}`}
                threadId={threadId}
                reportId={reportId}
                uniqueReportId={uniqueReportId}
                title={'研究报告'}
                initiallyOpened={false}
                stateUpdaters={{}}
                onSidebarOpen={() => {}}
                onSidebarClose={() => {}}
                activeReportCardId={null}
                status={'loading'}
                themeColor={1}
            />
        );
    };

    // 早期检测：如果 message 或 item.content 已经是 React 元素（被上游替换成组件），直接渲染它
    if (React.isValidElement(message)) {
        return (
            <>
                <div className="message-wrap" data-type={content_type}>
                    {message}
                </div>
            </>
        );
    }
    if (item && React.isValidElement(item.content)) {
        return (
            <>
                <div className="message-wrap" data-type={content_type}>
                    {item.content}
                </div>
            </>
        );
    }

    // 早期检测：如果消息或 item.content 中包含 report-notice 占位符，直接返回卡片渲染
    const earlyCandidateText =
        (typeof message === 'string' && message) ||
        (item && typeof item.answerContent === 'string' && item.answerContent) ||
        (item && typeof item.content === 'string' && item.content) ||
        (item && item.content && (item.content.html || item.content.text)) ||
        null;

    const earlyReport = tryRenderReportNotice(earlyCandidateText);
    if (earlyReport) {
        return (
            <>
                <div className="message-wrap" data-type={content_type}>
                    {earlyReport}
                </div>
            </>
        );
    }

    // 计算 planResultJSX 和标志
    let planResultJSX = null;
    let hasPlan = false;
    (() => {
        let candidateString = null;
        if (typeof message === 'string' && message.trim() !== '') {
            candidateString = message;
        } else if (item && typeof item === 'object') {
            if (typeof item.answerContent === 'string' && item.answerContent.trim() !== '') {
                candidateString = item.answerContent;
            } else if (typeof item.content === 'string' && item.content.trim() !== '') {
                candidateString = item.content;
            } else if (item.content && typeof item.content === 'object') {
                candidateString = item.content.html || item.content.text || null;
            }
        }
        const result = extractJSON(candidateString);
        if (result) {
            const { json: planData, start } = result;
            const displayMessage = candidateString ? candidateString.substring(0, start).trim() : '';
            // 与 useMessageRenderer.js 保持一致：以当前会话消息长度作为 len，判断 index === len - 1
            let isLatestPlan = true;
            try {
                isLatestPlan = isLatestMessage(index);
            } catch (e) {}
            planResultJSX = (
                <>
                    {displayMessage ? (
                        <GrtAiMarkdown
                            key={item._id || index}
                            side={item.side}
                            citation={{
                                side: item.side,
                                web_result: item.web_result,
                                retrieval_result: item.retrieval_result,
                                onClick: item.onClickCitation
                            }}
                            isDone={true}
                            className="ai-markdown-box"
                            content={displayMessage}
                        />
                    ) : null}
                    <ResearchPlanCard
                        skillStore={props.store?.skillStore || item.store?.skillStore || null}
                        planData={planData}
                        isLatestPlan={isLatestPlan}
                        sessionId={item.session_id || sessionId}
                        onEditPlan={() => {
                            if (typeof chatStore.toggleEditPlanMode === 'function') {
                                chatStore.toggleEditPlanMode && chatStore.toggleEditPlanMode();
                            }
                        }}
                        onStartResearch={() => {
                            // 立即禁用 plan 按钮，避免重复点击或在发送前继续操作
                            if (chatStore && typeof chatStore.setPlanButtonsDisabled === 'function') {
                                chatStore.setPlanButtonsDisabled(true);
                            }
                            if (chatStore && typeof chatStore.onSubmitMsg === 'function') {
                                chatStore.onSubmitMsg({ question: '太酷了！让我们开始吧。', value: 'start_research' });
                            }
                        }}
                    />
                </>
            );
            hasPlan = true;
        }
    })();

    return (
        <>
            <div className="message-wrap" data-type={content_type}>
                <Display show={(retrieval_result && retrieval_result?.data?.length) || (web_result && web_result?.data?.length)}>
                    <div className="ai-files-wrap" data-side={side}>
                        <Display show={retrieval_result && retrieval_result?.data?.length}>
                            <FileSearchDisplay
                                messageId={item._id || item.temId}
                                side={side}
                                type="retrieval"
                                total={retrieval_result?.count}
                                needSearch={false}
                                fileList={retrieval_result?.data}
                            />
                        </Display>
                        <Display show={web_result && web_result?.data?.length}>
                            <FileSearchDisplay
                                messageId={item._id || item.temId}
                                side={side}
                                type="web_search"
                                total={web_result?.count}
                                needSearch={false}
                                fileList={web_result?.data}
                            />
                        </Display>
                    </div>
                </Display>
            </div>

            {/*
                支持 ResearchPlanCard：如果消息或 item.answerContent 中包含研究计划的 JSON，则优先渲染卡片（兼容 chatStore 路径）
            */}
            {(() => {
                // 重用之前计算出的 planResultJSX，避免重复解析与渲染
                if (planResultJSX) {
                    hasPlan = true;
                    return planResultJSX;
                }
                return null;
            })()}
            {/* 如果上面的自执行函数返回了 plan JSX，则不要再渲染原始 message */}
            {(() => {
                const planResult = (() => null)();
                return null;
            })()}
            <Display show={personal_recommend_result}>
                <PersonalRecommend side={side} messageInfo={item} personal_recommend_result={personal_recommend_result} />
            </Display>
            {!hasPlan && typeof message === 'string' && message.trim() !== '' && (
                <GrtAiMarkdown
                    key={item._id || index}
                    side={side}
                    citation={{
                        side: side,
                        web_result: web_result,
                        retrieval_result: retrieval_result,
                        onClick: onClickCitation
                    }}
                    // 如果已有可显示的流式文本（message），优先展示，不再严格依赖 processing
                    isDone={!processing || Boolean(message)}
                    className="ai-markdown-box"
                    content={message}
                />
            )}
            <Display show={recommend_course_result}>
                <CourseRecommend side={side} recommend_course_result={recommend_course_result} />
            </Display>
            <Display show={intelligent_qa_result}>
                <IntelligentQA side={side} messageInfo={item} intelligent_qa_result={intelligent_qa_result} />
            </Display>

            <Display show={multimodal_learning_result || item?.multimodal_reference || null}>
                <MultimodalLearning
                    multimodal_learning_result={
                        multimodal_learning_result
                            ? multimodal_learning_result
                            : // 将 multimodal_reference 适配为 multimodal_learning_result 结构
                              {
                                  data: [],
                                  description: item?.multimodal_reference?.description || '',
                                  reference_id: item?.multimodal_reference?.reference_id || null,
                                  title: item?.multimodal_reference?.title || '多模态学习'
                              }
                    }
                    setDetailPanelOpen={setDetailPanelOpen}
                    setMultimodalReference={setMultimodalReference}
                    detailPanelOpen={detailPanelOpen}
                    activeDetailMessageId={activeDetailMessageId}
                    setActiveDetailMessageId={setActiveDetailMessageId}
                    messageId={item._id || item.temId || item.temIdStr || index}
                    agentName={currentAgent?.name || ''}
                />
            </Display>

            <Display show={ai_evaluation}>
                <Review messageInfo={item} ai_evaluation={ai_evaluation} />
            </Display>
            <Display show={card_result}>
                <CaseSimulation
                    key={item._id || index}
                    side={side}
                    getBaseParams={getBaseParams}
                    getCurSessionMsgs={getCurSessionMsgs}
                    messageInfo={item}
                    card_result={card_result}
                />
            </Display>
        </>
    );
};

export default inject('store')(observer(AiRender));
